import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { BreadcrumbNavigationComponent } from 'src/app/page_component_item/breadcrumb-navigation/breadcrumb-navigation.component';
import { breadcrumbNavigation } from 'src/app/page_component_item/breadcrumb-navigation/data/breadcrumbNavigation.interface';
import { ALL_SERVICE_TYPES, SERVICE_TYPES, SERVICE_TYPES_ENUM, serviceTypeInterface } from 'src/app/util/interfaces/requestClassification';
import { RequestCategorizationService } from './data/request-categorization.service';
import {
	CategoryCategorization,
	ScopeCategorization,
	TopicCategorization,
	MANDATORY_CI_BINDING,
} from './data/request-categorization.interface';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faAngleDown, faAngleRight, faAngleUp, faPen, faSave, faSquarePlus, faTimes, faTrash } from '@fortawesome/free-solid-svg-icons';
import { FormsModule } from '@angular/forms';
import { NotificationService } from 'src/app/services/notification/notification.service';
import { ConfirmButtonDirective } from 'src/app/util/directives/confirmButton/confirm-button.directive';
import { CheckboxComponent } from 'src/app/page_component_item/checkbox/checkbox.component';
import { MspButtonComponent } from 'src/app/page_component_item/msp-button/msp-button.component';

@Component({
	selector: 'app-request-categorization',
	standalone: true,
	imports: [
		CommonModule,
		BreadcrumbNavigationComponent,
		FontAwesomeModule,
		FormsModule,
		ConfirmButtonDirective,
		CheckboxComponent,
		MspButtonComponent,
	],
	templateUrl: './request-categorization.component.html',
	styleUrls: ['./request-categorization.component.scss', '../../../../../styles/tabs.styles.scss'],
})
export class RequestCategorizationComponent implements OnInit {
	private readonly requestCategorizationService = inject(RequestCategorizationService);
	private readonly notificationService = inject(NotificationService);

	icons = {
		faAngleUp: faAngleUp,
		faAngleDown: faAngleDown,
		faAngleRight: faAngleRight,
		faPen: faPen,
		faSave: faSave,
		faTrash: faTrash,
		faSquarePlus: faSquarePlus,
		faTimes: faTimes,
	};

	SERVICE_TYPES_ENUM = SERVICE_TYPES_ENUM;
	SERVICE_TYPES = ALL_SERVICE_TYPES;

	MANDATORY_CI_BINDING = MANDATORY_CI_BINDING;

	customerServiceTypes: serviceTypeInterface[] = [];
	customerSolutionAreas: serviceTypeInterface[] = [];
	serviceTypeChecked: string[] = [];

	breadcrumbNavigation: breadcrumbNavigation[] = [
		{ route: '/register', label: 'Cadastro' },
		{ route: '/register/categorization', label: 'Categorização' },
	];

	idSelectedServiceType: string;
	idSelectedSolutionArea: string;
	categorization: ScopeCategorization[];

	isLoadingCategorization = true;
	isUpdatingCategorization = false;

	ngOnInit(): void {
		this.requestCategorizationService.getServiceTypes().subscribe({
			next: (serviceTypes) => {
				this.customerServiceTypes = serviceTypes;
				this.serviceTypeChecked = this.customerServiceTypes.map((st) => st.id);
			},
		});

		this.requestCategorizationService.getSolutionAreas().subscribe({
			next: (solutionAreas) => {
				this.customerSolutionAreas = solutionAreas;
			},
		});
	}

	toggleServiceType(serviceType: SERVICE_TYPES) {
		if (this.serviceTypeChecked.includes(serviceType)) {
			this.serviceTypeChecked = this.serviceTypeChecked.filter((st) => st !== serviceType);
		} else {
			this.serviceTypeChecked.push(serviceType);
		}
	}

	getCategorization(idServiceType: string, getIdentMap: boolean = true) {
		if (!idServiceType || idServiceType === '') {
			return;
		}

		this.idSelectedServiceType = idServiceType;
		this.isLoadingCategorization = true;
		this.requestCategorizationService.getCategorization(idServiceType, this.idSelectedSolutionArea).subscribe({
			next: (categorizationResponse) => {
				this.categorization = categorizationResponse;
				this.isLoadingCategorization = false;
				if (getIdentMap) {
					this.transformCategorizationToIdentedMap(categorizationResponse);
				}
			},
		});
	}

	getSolutionAreaFilter(idSolutionArea: string) {
		this.idSelectedSolutionArea = idSolutionArea;
		if (this.idSelectedServiceType) {
			this.getCategorization(this.idSelectedServiceType);
		}
	}

	_categorizationTextMap = '';
	categorizationTextMap = '';

	transformCategorizationToIdentedMap(categorization: ScopeCategorization[]) {
		let identedMap = '';
		for (const scope of categorization) {
			identedMap += `#${scope.name}\n\t--id: ${scope.id}\n\t--mandatory: ${scope.mandatoryCiBinding}\n\t--firstLevel: ${scope.firstLevel}\n\t--monitoring: ${scope.monitoring}\n\t--active: ${scope.active}\n`;

			for (const category of scope.categories ?? []) {
				identedMap += `\t#${category.name}\n\t\t--id: ${category.id}\n\t\t--active: ${category.active}\n`;

				for (const topic of category.topics ?? []) {
					identedMap += `\t\t#${topic.name}\n\t\t\t--id: ${topic.id}\n\t\t\t--active: ${topic.active}\n`;
				}
			}
		}
		this.categorizationTextMap = identedMap;
		this._categorizationTextMap = identedMap.replaceAll('\n', String.raw`\n`).replaceAll('\t', String.raw`\t`);
	}

	handleIdentMap(event: Event) {
		const rawValue = (event.target as HTMLTextAreaElement).value;
		this.categorizationTextMap = rawValue;
		this._categorizationTextMap = rawValue.replaceAll('\n', String.raw`\n`).replaceAll('\t', String.raw`\t`);
	}

	handleIdentMapSpecialKeys(event: KeyboardEvent) {
		if (event.key === 'Enter') {
			event.preventDefault();
			this.categorizationTextMap += '\n\t';
			this._categorizationTextMap += String.raw`\n\t`;
		} else if (event.key === 'Tab') {
			event.preventDefault();
			this.categorizationTextMap += '\t';
			this._categorizationTextMap += String.raw`\t`;
		}
	}

	mapIdentMapToCategorization() {
		const categorization: ScopeCategorization[] = [];
		let currentScope: ScopeCategorization = null;
		let currentCategory: CategoryCategorization = null;
		let currentTopic: TopicCategorization = null;
		for (const [index, line] of this._categorizationTextMap.split(String.raw`\n`).entries()) {
			if (line.startsWith('#')) {
				currentScope = null;
				currentCategory = null;
				currentTopic = null;
				currentScope = {
					id: null,
					name: line.replace('#', '').trim(),
					label: line.replace('#', '').trim(),
					categories: [],
					mandatoryCiBinding: null,
					firstLevel: null,
					monitoring: null,
					active: null,
				};
				categorization.push(currentScope);
			}

			if (line.startsWith(String.raw`\t#`)) {
				currentCategory = null;
				currentTopic = null;
				currentCategory = {
					id: null,
					name: line.replace(String.raw`\t#`, '').trim(),
					label: line.replace(String.raw`\t#`, '').trim(),
					topics: [],
					active: null,
				};
				currentScope.categories.push(currentCategory);
			}

			if (line.startsWith(String.raw`\t\t#`)) {
				currentTopic = null;
				currentTopic = {
					id: null,
					name: line.replace(String.raw`\t\t#`, '').trim(),
					label: line.replace(String.raw`\t\t#`, '').trim(),
					active: null,
				};
				currentCategory.topics.push(currentTopic);
			}

			if (line.startsWith(String.raw`\t--`)) {
				const [key, value] = line
					.replace(String.raw`\t--`, '')
					.split(':')
					.map((s) => s.trim());
				if (currentScope && key === 'id') currentScope.id = value;
				if (currentScope && key === 'mandatory') currentScope.mandatoryCiBinding = Number.parseInt(value) as MANDATORY_CI_BINDING;
				if (currentScope && key === 'firstLevel') currentScope.firstLevel = value === '1' ? 1 : 0;
				if (currentScope && key === 'monitoring') currentScope.monitoring = value === '1' ? 1 : 0;
				if (currentScope && key === 'active') currentScope.active = value === '1' ? 1 : 0;
			}

			if (line.startsWith(String.raw`\t\t--`)) {
				const [key, value] = line
					.replace(String.raw`\t\t--`, '')
					.split(':')
					.map((s) => s.trim());
				if (currentCategory && key === 'id') currentCategory.id = value;
				if (currentCategory && key === 'active') currentCategory.active = value === '1' ? 1 : 0;
			}

			if (line.startsWith(String.raw`\t\t\t--`)) {
				const [key, value] = line
					.replace(String.raw`\t\t\t--`, '')
					.split(':')
					.map((s) => s.trim());
				if (currentTopic && key === 'id') currentTopic.id = value;
				if (currentTopic && key === 'active') currentTopic.active = value === '1' ? 1 : 0;
			}
		}
		return categorization;
	}

	saveIdentMap(): void {
		const categorization = this.mapIdentMapToCategorization();
		this.isUpdatingCategorization = true;
		this.requestCategorizationService.saveBulkCategorization(categorization, this.idSelectedServiceType).subscribe({
			next: (res) => {
				this.notificationService.setNotificationMessage('Mapeamento salvo com sucesso!');
				this.getCategorization(this.idSelectedServiceType);
				this.isUpdatingCategorization = false;
			},
			error: (error) => {
				this.isUpdatingCategorization = false;
				this.notificationService.setNotificationMessage('Erro ao salvar mapeamento.');
			},
		});
	}

	createScope(scope: ScopeCategorization) {
		this.isUpdatingCategorization = true;
		this.requestCategorizationService.createScope(scope).subscribe({
			next: (scopeRes) => {
				this.categorization.unshift(scopeRes);
				this.isUpdatingCategorization = false;
				this.transformCategorizationToIdentedMap(this.categorization);
				this.notificationService.setNotificationMessage('Competência criada com sucesso!');
			},
		});
	}

	updateScope(scope: ScopeCategorization) {
		this.isUpdatingCategorization = true;
		this.requestCategorizationService.patchScope(scope).subscribe({
			next: (res) => {
				this.isUpdatingCategorization = false;
				this.transformCategorizationToIdentedMap(this.categorization);
				this.notificationService.setNotificationMessage('Competência atualizada com sucesso!');
			},
			error: (error) => {
				this.isUpdatingCategorization = false;
				console.error('Erro ao atualizar competência', error);
				this.notificationService.setNotificationMessage('Erro ao atualizar competência.');
			},
		});
	}

	deleteScope(scope: ScopeCategorization) {
		this.isUpdatingCategorization = true;
		this.requestCategorizationService.deleteScope(scope).subscribe({
			next: (res) => {
				this.categorization = this.categorization.filter((s) => s.id !== scope.id);
				this.isUpdatingCategorization = false;
				this.transformCategorizationToIdentedMap(this.categorization);
				this.notificationService.setNotificationMessage('Competência deletada com sucesso!');
			},
		});
	}

	createCategory(category: CategoryCategorization, scope: ScopeCategorization, idServiceType: string) {
		this.isUpdatingCategorization = true;
		this.requestCategorizationService.createCategory(category, scope, idServiceType).subscribe({
			next: (categoryRes) => {
				scope.categories.unshift(categoryRes);
				this.isUpdatingCategorization = false;
				this.transformCategorizationToIdentedMap(this.categorization);
				this.notificationService.setNotificationMessage('Natureza criada com sucesso!');
			},
		});
	}

	updateCategory(category: CategoryCategorization) {
		this.isUpdatingCategorization = true;
		this.requestCategorizationService.patchCategory(category).subscribe({
			next: (res) => {
				this.isUpdatingCategorization = false;
				this.transformCategorizationToIdentedMap(this.categorization);
				this.notificationService.setNotificationMessage('Natureza atualizada com sucesso!');
			},
			error: (error) => {
				this.isUpdatingCategorization = false;
				console.error('Erro ao atualizar natureza', error);
				this.notificationService.setNotificationMessage('Erro ao atualizar natureza.');
			},
		});
	}

	deleteCategory(category: CategoryCategorization, scope: ScopeCategorization) {
		this.isUpdatingCategorization = true;
		this.requestCategorizationService.deleteCategory(category).subscribe({
			next: (res) => {
				scope.categories = scope.categories.filter((c) => c.id !== category.id);
				this.isUpdatingCategorization = false;
				this.transformCategorizationToIdentedMap(this.categorization);
				this.notificationService.setNotificationMessage('Natureza deletada com sucesso!');
			},
		});
	}

	createTopic(topic: TopicCategorization, category: CategoryCategorization) {
		this.isUpdatingCategorization = true;
		this.requestCategorizationService.createTopic(topic, category, this.idSelectedSolutionArea).subscribe({
			next: (topicRes) => {
				category.topics.unshift(topicRes);
				this.isUpdatingCategorization = false;
				this.transformCategorizationToIdentedMap(this.categorization);
				this.notificationService.setNotificationMessage('Tópico criado com sucesso!');
			},
		});
	}

	updateTopic(topic: TopicCategorization, category: CategoryCategorization) {
		this.isUpdatingCategorization = true;
		this.requestCategorizationService.patchTopic(topic, category, this.idSelectedSolutionArea).subscribe({
			next: (res) => {
				this.isUpdatingCategorization = false;
				this.transformCategorizationToIdentedMap(this.categorization);
				this.notificationService.setNotificationMessage('Tópico atualizado com sucesso!');
			},
			error: (error) => {
				this.isUpdatingCategorization = false;
				console.error('Erro ao atualizar tópico', error);
				this.notificationService.setNotificationMessage('Erro ao atualizar tópico.');
			},
		});
	}

	deleteTopic(topic: TopicCategorization, category: CategoryCategorization) {
		this.isUpdatingCategorization = true;
		this.requestCategorizationService.deleteTopic(topic).subscribe({
			next: (res) => {
				category.topics = category.topics.filter((t) => t.id !== topic.id);
				this.isUpdatingCategorization = false;
				this.transformCategorizationToIdentedMap(this.categorization);
				this.notificationService.setNotificationMessage('Tópico deletado com sucesso!');
			},
		});
	}
}
